import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Type from 'components/Type'
import Input from 'components/Input'
import Button from 'components/Button'
import Icon from 'components/Icon'
import Paper from 'components/Paper'
import Label from 'components/Label'
import A from 'components/A'
import Currency from 'components/Currency'
import ModalFlow from 'components/ModalFlow'
import TextButton from 'components/TextButton'
import Payment from 'components/CommonUI/Payment'
import IconList from 'components/CommonUI/IconList'
import CircleFrame from 'components/CommonUI/CircleFrame'
import DateRangePicker from 'components/DateRangePicker'
import PaperBottomButton from 'components/CommonUI/PaperBottomButton'
import { useVCC } from 'utils/actions'
import { find } from 'lodash'

const TYPES = [
  { purchase_type: 'air', icon: 'CelFlight', label: 'Flights', inlineLabel: 'flights' },
  { purchase_type: 'hotel', icon: 'CelStay', label: 'Stays', inlineLabel: 'stays' },
  { purchase_type: 'car', icon: 'CelCar', label: 'Rental Cars', inlineLabel: 'rentals' },
  { purchase_type: 'cruise', icon: 'CelCruise', label: 'Cruises', inlineLabel: 'cruises' },
  { purchase_type: 'unrestricted', icon: 'CelTourGuide', label: 'Tours & Experiences', inlineLabel: 'experiences' },
  { purchase_type: 'other', icon: 'CelOtherPurchase', label: 'It’s Something Else', inlineLabel: 'travel' },
]

// supplier_name, purchase_details, and purchase type (hotel, air, cruise, tour, other, car, insurance, unrestricted)

const CreateVirtualCard = ({
  defaultType,
  defaultVendor,

  vendorID,
  travelContentID,
  link,
}) => {
  const virtualCardConfirmation = useSelector(({ global }) => global.virtualCardConfirmation)
  const [editMode, setEditMode] = useState(false)
  const [activeIndex, setActiveIndex] = useState(defaultType?.length ? 1 : 0)
  const [paymentValues, setPaymentValues] = useState({})
  const {
    onRequestVCC,
    values,
    isVCCValid,
    onChange,
    emailError,
  } = useVCC({
    type: defaultType || 'general',
    vendor: defaultVendor || '',
    partner_uid: vendorID,
    travel_content_uid: travelContentID,
    paymentValues,
    cb: () => setActiveIndex(3),
  })
  const activeType = find(TYPES, { purchase_type: values.purchase_type })
  const confirmDisabled = 
    !values.full_name?.length || 
    !values.vendor?.length || 
    !values.confirmed_email?.length || 
    !values.purchase_details?.length || 
    !values.full_purchase_amount?.length ||
    (values.purchase_type === 'hotel' ? !values.start_date || !values.end_date : false) ||
    emailError

  const onSelect = data => {
    onChange({ purchase_type: data })
    setActiveIndex(1)
  }

  useEffect(() => {
    if (defaultType?.length) {
      onChange({ purchase_type: defaultType })
    }
  }, [defaultType])

  return (
    <ModalFlow
      activeIndex={activeIndex}
      setActiveIndex={setActiveIndex}
      sizeX="md"
    >
      {/* CHOOSE TYPE */}
      <Stack
        p={3}
        textAlign="center"
      >
        <Label
          color="primaryHue"
          mb={1.5}
        >
          Booking Type
        </Label>
        <Type
          variant="sectionLabel"
          mb={3}
        >
          What are you shopping for?
        </Type>
        <Stack
          display="grid"
          gridTemplateColumns="repeat(2, 1fr)"
          gap={1}
        >
          {TYPES.map(o => (
            <Paper
              key={`type_${o.purchase_type}`}
              border="transparent"
              borderHover="neutral_300"
              borderWidth="thick"
              round="extra"
              selectable
              onClick={() => onSelect(o.purchase_type)}
            >
              <Stack
                display="grid"
                justifyItems="center"
                justifyContent="center"
                textAlign="center"
                gap={1.5}
                p={1.5}
              >
                <CircleFrame>
                  <Icon
                    icon={o.icon}
                    width="50"
                    height="50"
                  />
                </CircleFrame>
                <Type
                  variant="data"
                >
                  {o.label}
                </Type>
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Stack>

      {/* PURCHASE DETAILS */}
      <Stack
        display="grid"
      >
        <Stack
          display="grid"
          p={3}
        >
          <CircleFrame
            justifySelf="center"
            mb={2}
          >
            <Icon
              icon="CelSendVirtualCard"
            />
          </CircleFrame>
          <Type
            variant="sectionLabel"
            textAlign="center"
            mb={3}
          >
            Pay with a virtual card
          </Type>
          <IconList
            align="flex-start"
            mb={3}
          >
            <span>
              {link ? (
                <>
                  <A href={link} color="tertiary" variant="inherit" inline>Shop with {defaultVendor}</A> & create a virtual card when you’re ready to check out
                </>
              ) : (
                <>
                  Shop for {activeType?.inlineLabel} & create a virtual card when you’re ready to check out
                </>
              )}
            </span>
            <span>
              Enter your booking details in the form below
            </span>
            <span>
              Complete your purchase with your virtual card
            </span>
          </IconList>
          <Stack
            gap={2}
            mb={3}
          >
            <Input
              label="Full Name"
              value={values.full_name}
              onChange={data => onChange({ full_name: data })}
            />
            <Input
              {...(defaultVendor ? {
                readOnly: true,
                endAdornment: <Box height="25px" width="20px"><Icon icon="Lock" /></Box>,
              } : {})}
              label="Vendor Name"
              placeholder="Who are you booking with?"
              value={values.vendor}
              onChange={data => onChange({ vendor: data })}
            />
            <Input
              label="Full Purchase Amount"
              optionalLabel="including taxes & fees"
              mask="currency_no_prefix"
              icon="$"
              iconColor="mainCool"
              removeMaskOnChange
              value={values.full_purchase_amount}
              onChange={data => onChange({ full_purchase_amount: data })}
            />
            {values.purchase_type === 'hotel' && (
              <Stack
                gap={1}
              >
                <Label>
                  Trip Dates
                </Label>
                <DateRangePicker
                  outlined
                  disablePast
                  value={[values.start_date, values.end_date]}
                  onChange={newValue => {
                    onChange({ start_date: newValue[0], end_date: newValue[1] })
                  }}
                />
              </Stack>
            )}
            <Input
              label="Purchase Description"
              // placeholder={vendorConfig.placeholder}
              value={values.purchase_details}
              onChange={data => onChange({ purchase_details: data })}
            />
            <Stack gap={0.5}>
              <Input
                label="Confirm Your Account Email"
                forceError={emailError}
                value={values.confirmed_email}
                onChange={data => onChange({ confirmed_email: data })}
              />
              {emailError && (
                <Type variant="labelMicroMedium" color="error">
                  Incorrect email address
                </Type>
              )}
            </Stack>
          </Stack>
          <Stack
            sx={theme => theme.internalMixins.columns({ gap: 1, justify: 'space-between' })}
          >
            {!defaultType && (
              <TextButton
                color="neutralCool_700"
                onClick={() => setActiveIndex(0)}
              >
                Go back
              </TextButton>
            )}
            {!!defaultType && <div />}
            <Button
              disabled={confirmDisabled}
              onClick={() => setActiveIndex(2)}
            >
              Continue
            </Button>
          </Stack>
        </Stack>
        {['car', 'hotel'].includes(values.purchase_type) && (
          <PaperBottomButton
            variant="error"
            noBorder
            noSelect
          >
            <Stack
              sx={theme => theme.internalMixins.columns({ gap: 1, justify: 'center' })}
            >
              <Icon
                icon="ErrorTriangleSmall"
              />
              <Type
                variant="labelMicroMedium"
                color="error"
              >
                Virtual cards will only work with Pay Now {values.purchase_type === 'hotel' ? 'hotels or stays' : 'car rentals'}
              </Type>
            </Stack>
          </PaperBottomButton>
        )}
      </Stack>

      {/* CONFIRM FUNDS */}
      <Stack
        p={3}
        gap={3}
      >
        <Stack
          display="grid"
          justifyItems="center"
          textAlign="center"
        >
          <CircleFrame
            mb={2}
          >
            <Icon
              icon="CelSendVirtualCard"
            />
          </CircleFrame>
          <Type
            variant="sectionLabel"
            mb={1.5}
          >
            Paying with a virtual card
          </Type>
          <Type
            variant="bodyLabelMedium"
          >
            Purchase amount:
            {' '}
            <Type variant="inherit" color="tertiary" inline><Currency amount={values.full_purchase_amount} /></Type>
          </Type>
        </Stack>
        <Payment
          hideCost
          hidePhone
          totalDue={values.full_purchase_amount}
          values={paymentValues}
          onChange={data => setPaymentValues(prev => ({ ...prev, ...data }))}
          onEditModeChange={setEditMode}
        />
        <Stack
          sx={theme => theme.internalMixins.columns({ gap: 1, justify: 'space-between' })}
        >
          <TextButton
            color="neutralCool_700"
            onClick={() => setActiveIndex(1)}
          >
            Go Back
          </TextButton>
          <Button
            disabled={!isVCCValid || editMode}
            onClick={() => {
              setActiveIndex(-1)
              onRequestVCC()
            }}
          >
            Confirm Purchase Info
          </Button>
        </Stack>
      </Stack>

      {/* COMPLETE */}
      <Stack
        p={3}
      >
        <Stack
          display="grid"
          justifyContent="center"
          justifyItems="center"
          textAlign="center"
        >
          <CircleFrame
            sx={{
              mb: '20px',
            }}
          >
            <Icon
              icon="CelSendVirtualCard"
            />
          </CircleFrame>
          <Type
            variant="sectionLabel"
            mb={2}
          >
            Virtual card was successfully created!
          </Type>
          <Type
            variant="p"
            mb={2}
          >
            You can now view and manage your card by visiting your virtual card page. To protect your funds, this card will automatically lock after 4 days.
          </Type>
        </Stack>
        <A
          to={`/wallet/virtual-card/${virtualCardConfirmation?.unique_id}`}
        >
          <Button
            justifyContent="center"
          >
            View My Virtual Card 
          </Button>
        </A>
        <Type
          variant="dataSmall"
          color="neutralCool_700"
          textAlign="center"
          mt={3}
        >
          <strong>NOTE:</strong> We’ve sent you an access link to {values.confirmed_email}. It can take several minutes for a virtual card to appear in your inbox.
        </Type>
      </Stack>
    </ModalFlow>
  )
}

CreateVirtualCard.propTypes = {
  defaultVendor: PropTypes.string,
  defaultType: PropTypes.string,
}

CreateVirtualCard.defaultProps = {
  drawerTitle: 'Create a New Card',
}

export default CreateVirtualCard